import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapView, type MapPin } from "../components/MapView";
import { MaIcon, MaTimePill } from "../components/ui";
import { markVisited } from "../components/layout/AuthGate";
import { useImportedEvents, useVenues } from "../lib/queries";

export function Onboarding1() {
  const navigate = useNavigate();
  const { data: venues = [] } = useVenues();
  const { data: events = [] } = useImportedEvents({ located: true, upcoming: true, limit: 50 });

  // Real Amsterdam pins — a sample of venues + imported events.
  const pins = useMemo<MapPin[]>(() => {
    const venuePins: MapPin[] = venues
      .filter((v) => v.lat != null && v.lng != null)
      .slice(0, 8)
      .map((v) => ({ id: `v:${v.id}`, lat: v.lat!, lng: v.lng!, cat: v.cat, live: v.live }));
    const eventPins: MapPin[] = events
      .filter((e) => e.lat != null && e.lng != null)
      .slice(0, 25)
      .map((e) => ({ id: `e:${e.id}`, lat: e.lat!, lng: e.lng!, cat: e.category ?? null, live: true }));
    return [...venuePins, ...eventPins];
  }, [venues, events]);

  const onContinue = () => {
    markVisited();
    navigate("/");
  };

  return (
    <div className="ma-screen">
      {/* Real OSM map, non-interactive — a teaser of what's behind the cover. */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 460, overflow: "hidden" }}>
        <MapView
          pins={pins}
          center={[4.9041, 52.3676]}
          zoom={12.2}
          fitToPins={pins.length > 4}
          interactive={false}
        />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, rgba(240,228,204,0.0) 40%, var(--ma-paper) 95%)",
          pointerEvents: "none",
        }} />
      </div>

      <div style={{
        position: "relative", zIndex: 2,
        padding: "calc(env(safe-area-inset-top, 20px) + 44px) 24px 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ma-ink)" }}>
          v1.0 · est. mmxxv
        </div>
        <MaTimePill />
      </div>

      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "var(--ma-paper)",
        padding: "40px 24px calc(env(safe-area-inset-bottom, 0px) + 36px)",
        zIndex: 3,
      }}>
        <div className="ma-display" style={{ fontSize: 72, lineHeight: 0.88, color: "var(--ma-ink)", marginBottom: 4 }}>
          myams<span style={{ color: "var(--ma-brick)" }}>.</span>
        </div>
        <div className="ma-mono" style={{
          fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--ma-ink-soft)", marginBottom: 28,
        }}>
          Amsterdam · happening · now
        </div>

        <div style={{
          fontFamily: "var(--ma-body)", fontSize: 17, lineHeight: 1.45, color: "var(--ma-ink)",
          marginBottom: 36, maxWidth: 320,
        }}>
          A pocket field guide to the city tonight. Map the gigs, the late kitchens, the markets that just opened, the rooms that just filled up.
        </div>

        <button
          onClick={onContinue}
          style={{
            width: "100%", padding: "18px 0", background: "var(--ma-ink)", color: "var(--ma-paper)",
            border: "none", borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 17,
            letterSpacing: "0.02em", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}
        >
          Show me Amsterdam
          <MaIcon name="arrow" size={18} stroke={2} color="var(--ma-paper)" />
        </button>
      </div>
    </div>
  );
}
